import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
  type ReactNode,
  type MutableRefObject,
} from 'react';

import { SCHEDULE_TYPES } from '@src/constants/commons';
import { removeEventTypeDuplicated } from '@src/containers/DropDownTriggers/constants';
import { ALLOWED_ROLES } from '@src/packages/utils/access';
import useGetDivisionsAllowed from '@src/packages/utils/hooks/useGetDivisionsAllowed';
import ScheduleService from '@src/services/ScheduleService';

import { DRAWER_TYPES } from './constants';
import { FilterValues } from '@src/components/FilterDropDown/FilterDropDown.interfaces';

type DrawerTypeValue = (typeof DRAWER_TYPES)[keyof typeof DRAWER_TYPES];

export interface ScheduleTrigger {
  id?: string;
  triggerId?: string;
  eventType?: { id?: string };
  [key: string]: unknown;
}

export type ScheduleTriggerWithDivision = ScheduleTrigger & { divId?: string };

interface ScheduleWithTriggers {
  divId?: string;
  triggers?: ScheduleTrigger[];
  [key: string]: unknown;
}

interface RefreshTimerController {
  pause?: () => void;
  resume?: () => void;
  reset?: () => void;
}

export interface ActiveDrawerState {
  type: DrawerTypeValue | null;
  data: unknown;
}

export interface DrawerActions {
  open: (type: DrawerTypeValue, data?: unknown) => void;
  close: () => void;
}

export interface RefreshTimerApi {
  pause: () => void;
  resume: () => void;
  reset: () => void;
}

export interface WidgetFilters {
  widgetId: string;
  filters: FilterValues | null;
  widget?: any;
}

export type WidgetFiltersByWidgetId = Record<string, WidgetFilters>;

export interface EventsByScheduleContextValue {
  loading: boolean;
  setLoading: React.Dispatch<React.SetStateAction<boolean>>;
  error: unknown;
  fetchEventsFromSchedules: (
    orgId: string,
    divId: string | undefined,
    formId: string | undefined,
  ) => Promise<ScheduleTriggerWithDivision[]>;
  eventsFromSchedules: ScheduleTriggerWithDivision[];
  refreshTimer: RefreshTimerApi;
  refreshTimerRef: MutableRefObject<RefreshTimerController | null>;
  registerRefreshTimer: (timerRef: RefreshTimerController | null) => void;
  drawerActions: DrawerActions;
  activeDrawer: ActiveDrawerState;
  DRAWER_TYPES: typeof DRAWER_TYPES;
  widgetFiltersByWidgetId: WidgetFiltersByWidgetId;
  setWidgetFiltersForWidget: (
    widgetId: string,
    update: React.SetStateAction<WidgetFilters | null>,
  ) => void;
  filtersPanelRef: MutableRefObject<{
    value: (typeof FILTERS_PANEL_VALUES)[keyof typeof FILTERS_PANEL_VALUES];
    widgetConfig: any;
    widget?: any;
  }>;
  activeFilterWidgetId: string | null;
  setActiveFilterWidgetId: React.Dispatch<React.SetStateAction<string | null>>;
}

export const EventsByScheduleContext = createContext<EventsByScheduleContextValue | null>(null);

export const useEventsByScheduleContext = (): EventsByScheduleContextValue => {
  const context = useContext(EventsByScheduleContext);
  if (!context) {
    throw new Error('useEventsByScheduleContext must be used within an EventsByScheduleProvider');
  }
  return context;
};

interface EventsByScheduleProviderProps {
  children: ReactNode;
}

export const FILTERS_PANEL_VALUES = {
  SIDE_BAR: 'sideBar',
  WIDGET: 'widget',
};

export const EventsByScheduleProvider = ({ children }: EventsByScheduleProviderProps) => {
  const [loading, setLoading] = useState(false);
  const [eventsFromSchedules, setEventsFromSchedules] = useState<ScheduleTriggerWithDivision[]>([]);
  const [error, setError] = useState<unknown>(null);

  const [widgetFiltersByWidgetId, setWidgetFiltersByWidgetId] = useState<WidgetFiltersByWidgetId>(
    {},
  );
  const [activeFilterWidgetId, setActiveFilterWidgetId] = useState<string | null>(null);

  const setWidgetFiltersForWidget = useCallback(
    (widgetId: string, update: React.SetStateAction<WidgetFilters | null>) => {
      if (!widgetId) return;
      setWidgetFiltersByWidgetId(prev => {
        const current = prev[widgetId];

        if (typeof update === 'function') {
          const next = (update as (p: WidgetFilters | undefined) => WidgetFilters | null)(current);
          if (!next) {
            if (!current) return prev;
            const { [widgetId]: _removed, ...rest } = prev;
            return rest;
          }
          return { ...prev, [widgetId]: { ...next, widgetId } };
        }

        if (!update) {
          if (!current) return prev;
          const { [widgetId]: _removed, ...rest } = prev;
          return rest;
        }

        return { ...prev, [widgetId]: { ...update, widgetId } };
      });
    },
    [],
  );
  const [activeDrawer, setActiveDrawer] = useState<ActiveDrawerState>({ type: null, data: null });

  const { allowedDivsIds } = useGetDivisionsAllowed(
    ALLOWED_ROLES.ORGANIZATIONS.DIVISIONS.DASHBOARDS.CHARTS.LIST,
  );

  const allowedDivsIdsRef = useRef<string[] | undefined>(allowedDivsIds);
  const refreshTimerRef = useRef<RefreshTimerController | null>(null);
  const filtersPanelRef = useRef({
    value: FILTERS_PANEL_VALUES.SIDE_BAR,
    widgetConfig: null,
    widget: null,
  });
  const scheduleService = useMemo(() => new ScheduleService(), []);
  const refreshTimer = useMemo<RefreshTimerApi>(
    () => ({
      pause: () => refreshTimerRef.current?.pause?.(),
      resume: () => refreshTimerRef.current?.resume?.(),
      reset: () => refreshTimerRef.current?.reset?.(),
    }),
    [],
  );
  const drawerActions = useMemo<DrawerActions>(
    () => ({
      open: (type, data = null) => {
        refreshTimer.pause();
        setActiveDrawer({ type, data });
      },
      close: () => {
        setActiveDrawer({ type: null, data: null });
        refreshTimer.resume();
      },
    }),
    [refreshTimer],
  );

  const allowedDivsIdsString = allowedDivsIds?.join(',');
  const prevAllowedDivsIdsString = allowedDivsIdsRef.current?.join(',');
  if (allowedDivsIdsString !== prevAllowedDivsIdsString) {
    allowedDivsIdsRef.current = allowedDivsIds;
  }

  const registerRefreshTimer = useCallback((timerRef: RefreshTimerController | null) => {
    refreshTimerRef.current = timerRef;
  }, []);

  const getSchedules = useCallback(
    async (orgId: string, divId: string | undefined) => {
      try {
        const params = {
          orgId,
          divIds: divId ? [divId] : allowedDivsIdsRef.current,
          where: [['type', '==', SCHEDULE_TYPES.schedule]],
        };
        const response = (await scheduleService.listFirestore(params)) as ScheduleWithTriggers[];
        return response || [];
      } catch (err) {
        setError(err);
        return [];
      }
    },
    [scheduleService],
  );

  const fetchEventsFromSchedules = useCallback(
    async (orgId: string, divId: string | undefined, formId: string | undefined) => {
      try {
        if (!formId || !orgId) return [];
        setLoading(true);
        const extractTriggersWithDivision = (schedule: ScheduleWithTriggers) => {
          if (!schedule?.triggers) return [];

          const schTriggers = schedule.triggers.map(trigger => ({
            ...trigger,
            divId: schedule.divId,
          }));

          return schTriggers as ScheduleTriggerWithDivision[];
        };

        const filterByFormId = (trigger: ScheduleTriggerWithDivision) => {
          if (!formId) return true;
          return trigger?.eventType?.id === formId;
        };

        const response = await getSchedules(orgId, divId);
        if (!response.length) return [];
        const events = response.flatMap(extractTriggersWithDivision).filter(filterByFormId);
        const uniqueTriggers = removeEventTypeDuplicated(events) as ScheduleTriggerWithDivision[];
        setEventsFromSchedules(uniqueTriggers);
        return events;
      } catch {
        setEventsFromSchedules([]);
        return [];
      } finally {
        setLoading(false);
      }
    },
    [getSchedules],
  );

  const value = useMemo<EventsByScheduleContextValue>(
    () => ({
      loading,
      setLoading,
      error,
      fetchEventsFromSchedules,
      eventsFromSchedules,
      refreshTimer,
      registerRefreshTimer,
      refreshTimerRef,
      drawerActions,
      activeDrawer,
      DRAWER_TYPES,
      widgetFiltersByWidgetId,
      setWidgetFiltersForWidget,
      filtersPanelRef,
      activeFilterWidgetId,
      setActiveFilterWidgetId,
    }),
    [
      loading,
      error,
      fetchEventsFromSchedules,
      eventsFromSchedules,
      refreshTimer,
      registerRefreshTimer,
      drawerActions,
      activeDrawer,
      widgetFiltersByWidgetId,
      setWidgetFiltersForWidget,
      filtersPanelRef,
      activeFilterWidgetId,
    ],
  );

  return (
    <EventsByScheduleContext.Provider value={value}>{children}</EventsByScheduleContext.Provider>
  );
};
